/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import { EmbeddedExperienceLoader, getEmbeddedSsoMode, useEmbeddedExperience } from '@coursera-web/embedded-experience';
import { StatsigProvider } from '@coursera-web/statsig';
import { compose } from 'recompose';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import connectToRouter from 'js/lib/connectToRouter';
import Retracked from 'js/lib/retracked';

import ClipsEmbeddedExperienceHeader from 'bundles/composable-page-header-consumer/header-renderers/ClipsEmbeddedExperienceHeader';
import DefaultHeader from 'bundles/composable-page-header-consumer/header-renderers/DefaultHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import useCourseBySlug from 'bundles/course-v2/hooks/useCourseBySlug';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import withFetchLazyImages from 'bundles/page/components/shared/utils/withFetchLazyImages';
import NotFound from 'bundles/phoenix/components/NotFound';
import { RedirectError } from 'bundles/ssr/lib/errors/directives';

import 'css!bundles/clips/styl/index';

type PropsFromStores = {
  csrfToken: string;
};

export type Props = PropsFromStores & {
  courseSlug: string;
};

export const ClipsApp = ({ children, courseSlug }: React.PropsWithChildren<Props>) => {
  const [componentDidMount, setComponentDidMount] = React.useState(false);
  const { isSingleItemEmbedView, isEmbeddedLaunchUrl, isHelpButtonDisabledForOrg } = useEmbeddedExperience();
  const isEmbedLayout = isSingleItemEmbedView || getEmbeddedSsoMode() != null;

  // Use modern Course query to get launch status
  const { courseData, error } = useCourseBySlug(courseSlug);

  React.useEffect(() => {
    if (courseData) {
      setComponentDidMount(true);
    }
  }, [courseData]);

  React.useEffect(() => {
    if (!componentDidMount) {
      // Check course launch status using modern schema
      if (courseData && courseData.isReal && !courseData.launchedAt) {
        // Course exists but is not launched yet - redirect to course home
        throw new RedirectError(301, `/learn/${courseSlug}`);
      }
    }
  }, [componentDidMount, courseData, courseSlug]);

  if (error) {
    return <NotFound />;
  }

  // Embed detection is client-only; show a loader until mount to avoid flashing the wrong header
  if (!componentDidMount && isEmbeddedLaunchUrl) {
    return <EmbeddedExperienceLoader />;
  }

  return (
    <StatsigProvider>
      <div className="rc-VideoLoggedOutApp" data-testid="clips-app">
        <UniversalPageFeatures hideChatWidget={isSingleItemEmbedView && isHelpButtonDisabledForOrg} />
        {isEmbedLayout ? <ClipsEmbeddedExperienceHeader /> : <DefaultHeader />}
        {componentDidMount && children}
        {!isEmbedLayout && <PageFooter />}
      </div>
    </StatsigProvider>
  );
};

export default compose<Props, {}>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      app: 'video_logged_out_page',
      page: 'video_lecture_landing_page',
    },
  })),
  connectToStores<{ csrfToken: string }>(['ApplicationStore'], ({ ApplicationStore }) => ({
    csrfToken: ApplicationStore.csrfToken,
  })),
  connectToRouter<{ courseSlug: string }>(({ params: { courseSlug } }) => ({
    courseSlug,
  })),
  withFetchLazyImages
)(ClipsApp);
